import { logError } from 'services/logging';

export const SW_FAILURE_BACKOFF_MS = 60 * 1000;

const TRANSIENT_SW_UPDATE_MESSAGES = [
  'newestWorker is null',
  'Failed to update a ServiceWorker',
  'An unknown error occurred when fetching the script',
  // WebKit's wording for the same condition as 'Failed to update a ServiceWorker': the document's
  // script execution context was stopped (tab closed, navigated away, or suspended into the
  // back-forward cache) while update() was in flight. Both the interval and the visibilitychange
  // path can fire into a page that is already going away, and by then there is nothing left to
  // update. The next tick after the page resumes registers the update normally.
  'Script execution context has stopped',
];

export const handleServiceWorkerUpdateError = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    const msg = (error as any).message;
    if (TRANSIENT_SW_UPDATE_MESSAGES.some((m) => msg.includes(m))) {
      console.warn('[SW] update skipped:', (error as any).name, msg);
      return;
    }
  }
  logError(error);
};

// Single-flight + post-failure back-off shared by interval and visibility paths.
export const createServiceWorkerUpdater = (registration: ServiceWorkerRegistration) => {
  let inFlight = false;
  let nextAllowedAt = 0;

  return async () => {
    if (inFlight) return;
    if (Date.now() < nextAllowedAt) return;
    if (registration.installing) return;
    if (!navigator.onLine) return;

    inFlight = true;
    try {
      await registration.update();
      nextAllowedAt = 0;
    } catch (error) {
      nextAllowedAt = Date.now() + SW_FAILURE_BACKOFF_MS;
      handleServiceWorkerUpdateError(error);
    } finally {
      inFlight = false;
    }
  };
};
